<template>
  <!-- Hoja del campo (o de la clave): diálogo dentro de .ed, sobre el editor desenfocado. A la izquierda
       lo que se edita (preguntas por orden de preferencia, regla y «sin valor»); a la derecha «Así se
       ejecutará», con las frases de recipe.js, la tabla por formulario y el ejemplo. No muta nada:
       emite y el editor aplica (con deshacer). -->
  <div class="fs-layer" @keydown="onKey">
    <div class="fs-back" aria-hidden="true" @click="onBack" />
    <section ref="cardRef" class="fs" :class="{ key: isKey }" role="dialog" aria-modal="true" :aria-labelledby="uid + '-t'">
      <header class="fs-head">
        <div class="fs-top">
          <div v-if="pos && pos.n > 1" class="fs-nav">
            <button ref="prevRef" type="button" class="fs-navb" :disabled="pos.i <= 1" :title="t('fs.prev')" :aria-label="t('fs.prev')"
                    @click="nav(-1)">‹</button>
            <span class="fs-pos">{{ pos.i }} / {{ pos.n }}</span>
            <button ref="nextRef" type="button" class="fs-navb" :disabled="pos.i >= pos.n" :title="t('fs.next')" :aria-label="t('fs.next')"
                    @click="nav(1)">›</button>
          </div>
          <span class="mc-ico entity" aria-hidden="true">E</span>
          <h2 :id="uid + '-t'" ref="titleRef" class="fs-title" tabindex="-1">
            <span class="fs-ent">{{ entityName }}</span><span class="fs-crumb" aria-hidden="true">›</span><span class="fs-fname">{{ field.name }}</span>
          </h2>
          <span class="fs-type"><b>{{ glyph }}</b>{{ typeName }}</span>
          <span v-if="field.column" class="fs-colname">{{ t('fs.column', { c: field.column }) }}</span>
          <span class="grow" />
          <span v-if="isKey" class="fs-keytag">{{ t('fs.keyTitle', { what: t('key.' + field.keyRole) }) }}</span>
          <button v-else-if="warnings.length" type="button" class="fs-wcount" :class="warnLevel" @click="toWarnings">
            <span aria-hidden="true">{{ warnLevel === 'danger' ? '■' : '▲' }}</span>
            {{ warnings.length === 1 ? t('fs.warnOne') : t('fs.warnCount', { n: warnings.length }) }}
          </button>
          <button type="button" class="fs-x" :aria-label="t('ed.close')" :title="t('ed.close')" @click="emit('close')">✕</button>
        </div>
        <p v-if="!isKey && recipe.line" class="fs-line">{{ recipe.line }}</p>
      </header>

      <!-- ── Campo de la clave (B.3) ──────────────────────────────────────────── -->
      <div v-if="isKey" ref="bodyRef" class="fs-body one">
        <div class="fs-right">
          <p v-for="s in keyIntro" :key="s.code" class="fs-s lead">{{ s.text }}</p>
          <h4 class="fs-glabel">{{ t('rc.pf.title') }}</h4>
          <ul class="fs-keyrows">
            <li v-for="r in recipe.keyRows || []" :key="r.questId" :class="r.level">
              <span class="fs-p">P{{ r.priority || 1 }}</span>
              <span class="fs-pfn" :title="r.questId">{{ r.name }}</span>
              <span class="fs-kt">{{ r.text }}</span>
              <button type="button" class="btn ghost fs-sm" @click="emit('edit-source', { questId: r.questId, field: field.keyRole === 'id' ? 'idAlias' : 'origin' })">
                {{ t('fs.editSource') }}
              </button>
            </li>
          </ul>
          <div class="fs-gb fs-keyrest">
            <template v-for="s in keyRest" :key="s.code">
              <p v-if="s.level === 'text'" class="fs-s">{{ s.text }}</p>
              <p v-else-if="s.level === 'info'" class="fs-s info"><i class="fs-i" aria-hidden="true">i</i><span>{{ s.text }}</span></p>
              <p v-else class="fs-warn" :class="s.level"><b aria-hidden="true">{{ s.level === 'danger' ? '■' : '▲' }}</b><span>{{ s.text }}</span></p>
            </template>
          </div>
        </div>
      </div>

      <!-- ── Campo normal (B.2) ───────────────────────────────────────────────── -->
      <div v-else ref="bodyRef" class="fs-body">
        <div ref="leftRef" class="fs-left">
          <section class="fs-sec" :aria-labelledby="uid + '-c'">
            <h3 :id="uid + '-c'" class="fs-h"><span class="fs-n">1</span>{{ t('fs.chain') }}</h3>
            <ol v-if="chain.length" class="fs-chain">
              <template v-for="(c, i) in chain" :key="c.alias">
                <li :ref="(el) => { cardEls[i] = el; }" class="fs-q" :class="{ sys: c.sys, dead: c.dead }" tabindex="0"
                    aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown Enter Delete" :aria-label="cardLabel(c, i)"
                    :title="c.dead ? t('cv.deadTitle') : undefined" @keydown="onCardKey($event, c, i)">
                  <span class="fs-qn" aria-hidden="true">{{ i + 1 }}</span>
                  <span class="fs-qmain">
                    <span class="fs-ql">{{ c.sys ? c.label : quote(c) }}<span v-if="c.sys" class="fs-tag">{{ t('cv.tagSys') }}</span></span>
                    <span v-if="c.sys || (c.label && c.label !== c.alias)" class="fs-qa">{{ c.alias }}</span>
                    <span v-if="!c.sys && c.cov" class="fs-qcov">
                      <span v-if="c.cov.dots" class="mc-cov fs-cov" aria-hidden="true"><i v-for="(d, j) in c.cov.dots" :key="j" :class="d.state" :style="{ '--fc': d.color }" :title="d.title">{{ d.code }}</i></span>
                      <span v-else class="fs-bar" aria-hidden="true"><b :style="{ width: (c.cov.N ? 100 * c.cov.k / c.cov.N : 0) + '%' }" /></span>
                      {{ c.dead ? t('cv.deadTitle') : t('fs.carried', { k: c.cov.k, N: c.cov.N }) }}
                    </span>
                  </span>
                  <span class="fs-qbtns">
                    <button type="button" class="fs-ib" tabindex="-1" :disabled="i === 0" :title="t('fs.up')" :aria-label="t('fs.up')"
                            @click="reorder(i, i - 1)">↑</button>
                    <button type="button" class="fs-ib" tabindex="-1" :disabled="i === chain.length - 1" :title="t('fs.down')" :aria-label="t('fs.down')"
                            @click="reorder(i, i + 1)">↓</button>
                    <button type="button" class="fs-ib" tabindex="-1" :title="t('fs.change')" :aria-label="t('fs.change')"
                            @click="openPick('replace', i)">⇄</button>
                    <button type="button" class="fs-ib del" tabindex="-1" :title="t('fs.remove')" :aria-label="t('fs.remove')"
                            @click="remove(c, i)">⌫</button>
                  </span>
                </li>
                <li v-if="picking && picking.mode === 'replace' && picking.pos === i" class="fs-pick">
                  <PickList ref="pickRef" :options="aliasOptions" :placeholder="t('pk.search')" :empty-text="t('pk.none')" autofocus
                            @pick="onPick" @close="closePick" />
                </li>
              </template>
            </ol>
            <p v-else class="fs-empty">{{ t('rc.none') }}</p>

            <div v-if="picking && picking.mode === 'add'" class="fs-pick">
              <PickList ref="pickRef" :options="aliasOptions" :placeholder="t('pk.search')" :empty-text="t('pk.none')" autofocus
                        @pick="onPick" @close="closePick" />
            </div>
            <template v-else>
              <button ref="addRef" type="button" class="fs-add" @click="openPick('add')">{{ t('fs.add') }}</button>
              <div v-if="suggested.length" class="fs-sugg">
                <span class="fs-sugg-l"><span aria-hidden="true">✦</span> {{ t('fs.suggested') }}</span>
                <button v-for="o in suggested" :key="o.value" type="button" class="fs-sugg-b" :title="o.sub || o.value"
                        @click="add(o.value)">«{{ o.label }}»</button>
              </div>
            </template>
            <p v-if="chain.length > 1" class="fs-hint">{{ t('fs.chainHint') }}</p>
          </section>

          <fieldset class="fs-sec">
            <legend class="fs-h"><span class="fs-n">2</span>{{ t('ins.merge') }}</legend>
            <div class="fs-cards">
              <label v-for="m in MODES" :key="m" class="fs-card" :class="{ on: mode === m }">
                <input type="radio" class="fs-radio" :name="uid + '-m'" :value="m" :checked="mode === m"
                       @change="emit('rule', { mergeMode: m })" />
                <span class="fs-ct"><i class="fs-dot" aria-hidden="true" />{{ t('merge.' + m) }}</span>
                <span class="fs-cd">{{ t('merge.' + m + '.d') }}</span>
                <span class="fs-mini" :class="{ warn: minis[m].warn }">{{ minis[m].seq }} ⇒ <b>{{ minis[m].res }}</b></span>
              </label>
            </div>
          </fieldset>

          <fieldset class="fs-sec">
            <legend class="fs-h"><span class="fs-n">3</span>{{ t('fs.missing') }}</legend>
            <div class="fs-cards">
              <label v-for="o in MISSING" :key="o" class="fs-card" :class="{ on: onMissing === o }">
                <input type="radio" class="fs-radio" :name="uid + '-o'" :value="o" :checked="onMissing === o"
                       @change="emit('rule', { onMissing: o })" />
                <span class="fs-ct"><i class="fs-dot" aria-hidden="true" />{{ t('missing.' + o) }}</span>
                <span class="fs-cd">{{ t(o === 'keep' ? 'fs.missKeep.d' : 'fs.missClear.d') }}</span>
              </label>
            </div>
          </fieldset>
        </div>

        <div ref="rightRef" class="fs-right" aria-live="polite">
          <h3 class="fs-h run">{{ t('fs.run') }}</h3>
          <div class="fs-groups">
            <div v-for="g in groups" :key="g.code" class="fs-g">
              <div class="fs-gl">{{ t('fs.g.' + g.code) }}</div>
              <div class="fs-gb">
                <p v-for="s in g.main" :key="s.code + s.text" class="fs-s">{{ s.text }}</p>
                <ul v-if="g.code === 'C' && samples.length" class="fs-samples">
                  <li v-for="sm in samples" :key="sm.raw" :class="sm.kind" :title="sm.title">
                    <span class="fs-sraw">«{{ sm.raw }}»</span><b aria-hidden="true">{{ sm.mark }}</b><span v-if="sm.value">{{ sm.value }}</span>
                  </li>
                </ul>
                <template v-for="s in g.rest" :key="s.code + s.text">
                  <p v-if="s.level === 'info'" class="fs-s info"><i class="fs-i" aria-hidden="true">i</i><span>{{ s.text }}</span></p>
                  <p v-else class="fs-warn" :class="s.level"><b aria-hidden="true">{{ s.level === 'danger' ? '■' : '▲' }}</b><span>{{ s.text }}</span></p>
                </template>
              </div>
            </div>
          </div>

          <section v-if="pfRows.length" class="fs-block">
            <h4 class="fs-glabel">{{ t('rc.pf.title') }}</h4>
            <ul class="fs-pft">
              <li v-for="row in pfRows" :key="row.key" class="fs-pfr" :class="row.state">
                <span class="fs-p">P{{ row.priority }}</span>
                <span class="fs-pfn" :title="row.title">{{ row.label }}</span>
                <span class="fs-pfu">{{ usesText(row) }}</span>
                <span class="fs-pfm" :class="{ danger: row.onMissing === 'clearsAlways' }">{{ missText(row) }}</span>
                <span class="fs-pfe">
                  <span v-if="row.leads" class="fs-lead">{{ t('rc.pf.leads') }}</span>
                  <button v-if="row.names" type="button" class="fs-show" :aria-expanded="openGroups.includes(row.key)"
                          @click="toggleGroup(row.key)">{{ t('rc.pf.show') }}<span aria-hidden="true"> ▾</span></button>
                </span>
                <ul v-if="row.names && openGroups.includes(row.key)" class="fs-pfsub">
                  <li v-for="n in row.names" :key="n.questId" :title="n.questId">{{ n.name }}</li>
                </ul>
              </li>
            </ul>
          </section>

          <RecipeExample v-if="recipe.example" :seed="recipe.example" :rule="ruleNow" :forms="recipe.perForm || []"
                         @rule="emit('rule', $event)" />
          <p v-else-if="chain.length" class="fs-noex">{{ t('ex.none') }}</p>

          <details class="fs-notes">
            <summary>{{ t('rc.note.title') }}</summary>
            <ul>
              <li v-for="k in NOTES" :key="k">{{ t('rc.note.' + k) }}</li>
            </ul>
          </details>
        </div>
      </div>

      <footer class="fs-foot">
        <p v-if="saved && !isKey" class="fs-past"><i class="fs-i" aria-hidden="true">i</i><span>{{ t('rc.note.past') }}</span></p>
        <div class="fs-actions">
          <div class="fs-tools" />
          <button v-if="!isKey && chain.length" type="button" class="btn danger" @click="emit('clear-all')">{{ t('fs.removeAll') }}</button>
          <button v-if="!isKey && changed" type="button" class="btn" @click="emit('revert')">{{ t('fs.revert') }}</button>
          <span class="grow" />
          <button type="button" class="btn primary fs-done" @click="emit('close')">{{ t('fs.done') }}</button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import PickList from '@vem/components/PickList.vue';
import RecipeExample from '@vem/components/RecipeExample.vue';
import { t } from '@vem/i18n.js';
import { shortName } from '@vem/catalog.js';
import { CONV_SAMPLES, showValue, typeFamily } from '@vem/recipe.js';

let seq = 0;

const props = defineProps({
  /** {id, cls, name, type, column, table, isExt, rootName, keyRole: 'id'|'origin'|null}: el mismo `field` de buildRecipe. */
  field: { type: Object, required: true },
  /** buildRecipe(...) del campo. */
  recipe: { type: Object, required: true },
  /** Conexiones del campo en orden de preferencia: [{index (en dto.maps), alias, label, sys, dead, cov: {k, N, dots}|null}]. */
  chain: { type: Array, default: () => [] },
  /** Regla del campo {mergeMode, onMissing}; null = last/keep. */
  rule: { type: Object, default: null },
  /** Opciones del PickList de preguntas (grupo «Sugeridas» primero). */
  aliasOptions: { type: Array, default: () => [] },
  /** Posición del campo entre las filas visibles del lienzo (1-based). */
  pos: { type: Object, default: null },
  /** La definición ya existe (dto.id): muestra rc.note.past. */
  saved: { type: Boolean, default: false },
  /** Fila pulsada, en coordenadas de .ed: origen de la animación de entrada. */
  origin: { type: Object, default: null },
});
const emit = defineEmits(['close', 'nav', 'rule', 'reorder', 'add', 'replace', 'remove', 'clear-all', 'revert', 'edit-source']);

const MODES = ['last', 'first', 'priority', 'concat'];
const MISSING = ['keep', 'clear'];
const NOTES = ['order', 'noop', 'lateRetry', 'reactivate'];
const GROUPS = ['A', 'C', 'R', 'M', 'X'];
const uid = `fs-${++seq}`;

const cardRef = ref(null);
const titleRef = ref(null);
const leftRef = ref(null);
const rightRef = ref(null);
const bodyRef = ref(null);
const addRef = ref(null);
const prevRef = ref(null);
const nextRef = ref(null);
const pickRef = ref(null);
const cardEls = [];
/** Desplegable abierto: null | {mode: 'add'} | {mode: 'replace', pos, index}. */
const picking = ref(null);
const openGroups = ref([]);

const isKey = computed(() => !!props.field.keyRole);
const ruleNow = computed(() => props.rule || { mergeMode: 'last', onMissing: 'keep' });
const mode = computed(() => ruleNow.value.mergeMode || 'last');
const onMissing = computed(() => ruleNow.value.onMissing || 'keep');
const family = computed(() => typeFamily(props.field.type));
const typeName = computed(() => (family.value === 'unsupported' ? shortName(props.field.type) || '?' : t('type.' + family.value)));
const glyph = computed(() => t('glyph.' + (family.value === 'dateAny' ? 'date' : family.value)));
const entityName = computed(() => props.field.entityName || shortName(props.field.cls));
const sentences = computed(() => props.recipe.sentences || []);
const warnings = computed(() => props.recipe.warnings || []);
const warnLevel = computed(() => (warnings.value.some((w) => w.level === 'danger') ? 'danger' : 'warn'));

const quote = (c) => `«${c.label || c.alias}»`;

/** Frases por grupo (A · C · R · M · X): primero las de texto, luego notas y avisos, en su orden. */
const groups = computed(() => GROUPS.map((code) => {
  const items = sentences.value.filter((s) => s.group === code);
  return { code, main: items.filter((s) => s.level === 'text'), rest: items.filter((s) => s.level !== 'text') };
}).filter((g) => g.main.length || g.rest.length));

const keySentences = computed(() => sentences.value.filter((s) => s.group === 'K'));
const keyIntro = computed(() => keySentences.value.slice(0, 1));
const keyRest = computed(() => keySentences.value.slice(1));

/** Ejemplos ✓/✗/! de la conversión del tipo del campo (el valor convertido, si cambia la grafía). */
const SAMPLE = { ok: ['✓', 'conv.ok'], bad: ['✗', 'conv.bad'], warn: ['!', 'conv.thousands'] };
const samples = computed(() => (CONV_SAMPLES[family.value] || []).map((s) => {
  const shown = s.ok ? showValue(family.value, s.value) : '';
  return { raw: s.raw, kind: s.mark, mark: SAMPLE[s.mark][0], title: t(SAMPLE[s.mark][1]), value: shown !== s.raw ? shown : '' };
}));

/** Mini-ejemplos de las tarjetas de regla: A (menos prioridad) · B (más) · C (menos). */
const minis = computed(() => {
  const feeds = (props.recipe.perForm || []).filter((f) => f.state === 'feeds');
  const ladder = [...new Set(feeds.map((f) => f.priority || 1))].sort((a, b) => a - b);
  const multi = ladder.length >= 2;
  const lo = 'P' + ladder[ladder.length - 1];
  const hi = 'P' + ladder[0];
  const seq = multi ? `${lo} A · ${hi} B · ${lo} C` : 'A · B · C';
  const isText = family.value === 'text';
  return {
    last: { seq, res: 'C' },
    first: { seq, res: 'A' },
    priority: multi ? { seq, res: 'B' } : { seq: `${t('rc.mini.asLast')} · ${seq}`, res: 'C', warn: feeds.length >= 2 },
    concat: isText ? { seq, res: '|A|B|C|' } : { seq: `${t('rc.mini.asLast')} · ${seq}`, res: 'C', warn: true },
  };
});

const suggested = computed(() => props.aliasOptions.filter((o) => o.group === t('fs.suggested') && !o.disabled));

/** Filas «Por formulario»; con más de 8 fuentes, las idénticas se agrupan. */
const pfRows = computed(() => {
  const list = props.recipe.perForm || [];
  const one = (f) => ({ ...f, key: f.questId, priority: f.priority || 1, label: f.name, title: f.questId });
  if (list.length <= 8) return list.map(one);
  const byKey = new Map();
  for (const f of list) {
    const k = [f.priority || 1, f.state, (f.uses || []).join('|'), f.onMissing, f.leads].join('#');
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(f);
  }
  return [...byKey.entries()].map(([k, fs]) => (fs.length === 1 ? one(fs[0]) : {
    ...fs[0], key: k, priority: fs[0].priority || 1, label: t('rc.pf.group', { n: fs.length }), title: '',
    names: fs.map((f) => ({ questId: f.questId, name: f.name })),
  }));
});

function usesText(row) {
  if (row.state === 'feeds') return t('rc.pf.uses', { chain: (row.uses || []).join(' › ') });
  return t('rc.pf.' + row.state);
}

function missText(row) {
  return row.state === 'feeds' || row.state === 'silent' ? t('rc.pf.' + row.onMissing) : '';
}

function toggleGroup(key) {
  openGroups.value = openGroups.value.includes(key) ? openGroups.value.filter((k) => k !== key) : [...openGroups.value, key];
}

// «Deshacer cambios del campo» solo cuando sus preguntas o su regla difieren de cómo estaban al abrirlo.
const sig = () => JSON.stringify([props.chain.map((c) => c.alias), mode.value, onMissing.value]);
const initialSig = ref(sig());
const changed = computed(() => sig() !== initialSig.value);

function cardLabel(c, i) {
  const what = c.sys ? `${c.label} (${t('cv.tagSys')})` : quote(c);
  const cov = c.sys || !c.cov ? '' : ` · ${c.dead ? t('cv.deadTitle') : t('fs.carried', { k: c.cov.k, N: c.cov.N })}`;
  return `${i + 1}. ${what} · ${c.alias}${cov}`;
}

// ── Acciones (emiten; el editor aplica) ─────────────────────────────────────
async function focusCard(i) {
  await nextTick();
  const n = props.chain.length;
  if (!n) addRef.value?.focus();
  else cardEls[Math.max(0, Math.min(i, n - 1))]?.focus();
}

function reorder(from, to) {
  if (to < 0 || to >= props.chain.length) return;
  emit('reorder', { from, to });
  focusCard(to);
}

function remove(c, i) {
  emit('remove', c.index);
  focusCard(i);
}

function add(alias) {
  emit('add', alias);
  focusCard(props.chain.length);
}

function openPick(m, i) {
  picking.value = m === 'replace' ? { mode: m, pos: i, index: props.chain[i].index } : { mode: m };
  nextTick(() => [].concat(pickRef.value || [])[0]?.focus());
}

function closePick() {
  const p = picking.value;
  picking.value = null;
  if (p && p.mode === 'replace') focusCard(p.pos);
  else nextTick(() => addRef.value?.focus());
}

function onPick(alias) {
  const p = picking.value;
  picking.value = null;
  if (p.mode === 'replace') {
    emit('replace', { index: p.index, alias });
    focusCard(p.pos);
  } else {
    add(alias);
  }
}

function nav(d) {
  if (!props.pos) return;
  const i = props.pos.i + d;
  if (i >= 1 && i <= props.pos.n) emit('nav', d);
}

function toWarnings() {
  rightRef.value?.querySelector('.fs-warn')?.scrollIntoView({ block: 'center' });
}

function onBack() {
  if (picking.value) closePick();
  else emit('close');
}

// ── Teclado ──────────────────────────────────────────────────────────────────
function onCardKey(e, c, i) {
  if (e.target !== e.currentTarget) return;
  if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) reorder(i, e.key === 'ArrowUp' ? i - 1 : i + 1);
  else if (e.key === 'Delete') remove(c, i);
  else if (e.key === 'Enter' && !e.altKey && !e.ctrlKey && !e.metaKey) openPick('replace', i);
  else return;
  e.preventDefault();
}

/** Esc por capas: primero el desplegable (y el foco vuelve a su disparador); si no, la hoja. */
function onKey(e) {
  if (e.key === 'Escape') {
    e.stopPropagation();
    e.preventDefault();
    if (picking.value) closePick();
    else if (!(e.target.closest && e.target.closest('.fs-pick'))) emit('close');
    return;
  }
  if (e.altKey && (e.key === 'PageUp' || e.key === 'PageDown')) {
    e.preventDefault();
    nav(e.key === 'PageUp' ? -1 : 1);
  }
}

/** Foco al abrir (o al pasar de campo): el título; sin conexiones, el buscador con las sugeridas. */
function focusStart() {
  if (!isKey.value && !props.chain.length) {
    openPick('add');
    return;
  }
  const a = document.activeElement;
  if (a && (a === prevRef.value || a === nextRef.value) && !a.disabled) return;
  titleRef.value?.focus({ preventScroll: true });
}

watch(() => props.field.id, async () => {
  initialSig.value = sig();
  picking.value = null;
  openGroups.value = [];
  await nextTick();
  [bodyRef.value, leftRef.value, rightRef.value].forEach((el) => { if (el) el.scrollTop = 0; });
  focusStart();
});

onMounted(() => {
  const card = cardRef.value;
  if (props.origin && card) {
    card.style.transformOrigin = `${props.origin.x - card.offsetLeft}px ${props.origin.y - card.offsetTop}px`;
  }
  focusStart();
});
</script>

<style>
/* ── Hoja del campo: capa dentro de .ed (el padre desenfoca barra y rejilla con .sheet-open) ── */
.vem-root .fs-layer {
  position: absolute; inset: 0; z-index: 30; display: grid; place-items: center; padding: 16px;
  container: fs / inline-size;
}
.vem-root .fs-back { position: absolute; inset: 0; background: var(--scrim, rgba(244, 246, 247, .55)); }
.vem-root .fs {
  position: relative; display: grid; grid-template-rows: auto minmax(0, 1fr) auto; width: min(1080px, 100%); height: min(860px, 100%);
  background: var(--panel); border: 1px solid var(--line2); border-top: 3px solid var(--lime); border-radius: 18px;
  box-shadow: var(--sheet-shadow, 0 30px 80px -24px rgba(31, 35, 40, .38)); overflow: hidden;
}
.vem-root .fs-enter-active .fs, .vem-root .fs-leave-active .fs { transition: opacity .18s ease, transform .18s cubic-bezier(.2, .8, .2, 1); }
.vem-root .fs-enter-active .fs-back, .vem-root .fs-leave-active .fs-back { transition: opacity .18s ease; }
.vem-root .fs-enter-from .fs, .vem-root .fs-leave-to .fs { opacity: 0; transform: scale(.96); }
.vem-root .fs-enter-from .fs-back, .vem-root .fs-leave-to .fs-back { opacity: 0; }

/* cabecera */
.vem-root .fs-head { padding: 12px 16px 12px 18px; border-bottom: 1px solid var(--line2); background: linear-gradient(180deg, var(--lime-bg), transparent 140%); }
.vem-root .fs-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; }
.vem-root .fs-nav { display: inline-flex; align-items: center; gap: 2px; padding: 2px; border: 1px solid var(--line2); border-radius: 100px; background: var(--panel); }
.vem-root .fs-navb {
  display: grid; place-items: center; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 50%;
  background: none; color: var(--tx2); font-size: 17px; line-height: 1; cursor: pointer;
}
.vem-root .fs-navb:hover:not(:disabled) { background: var(--hover); color: var(--tx); }
.vem-root .fs-navb:disabled { opacity: .3; cursor: default; }
.vem-root .fs-pos { font-family: var(--mono); font-size: 11px; color: var(--tx2); padding: 0 3px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.vem-root .fs-title {
  display: flex; align-items: baseline; gap: 7px; min-width: 0; margin: 0; font-size: 17px; font-weight: 600;
  letter-spacing: -.01em; border-radius: 6px; outline: none; overflow-wrap: anywhere;
}
.vem-root .fs-title:focus, .vem-root .fs-title:focus-visible { outline: none; box-shadow: none; }
.vem-root .fs-ent { color: var(--lime); }
.vem-root .fs-crumb { color: var(--tx3); font-weight: 400; }
.vem-root .fs-fname { font-family: var(--mono); font-size: 16px; }
.vem-root .fs-type {
  display: inline-flex; align-items: center; gap: 6px; padding: 2px 9px 2px 3px; border: 1px solid var(--line2);
  border-radius: 100px; background: var(--panel); font-size: 11.5px; color: var(--tx2); white-space: nowrap;
}
.vem-root .fs-type b { font-family: var(--mono); font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 100px; background: var(--mute-bg); color: var(--tx); }
.vem-root .fs-colname { font-family: var(--mono); font-size: 11px; color: var(--tx3); white-space: nowrap; }
.vem-root .fs-keytag {
  font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase;
  color: var(--lime); background: var(--lime-bg); border-radius: 6px; padding: 4px 8px; white-space: nowrap;
}
.vem-root .fs-wcount {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 11px; border-radius: 100px; cursor: pointer; white-space: nowrap;
  font-size: 12px; font-weight: 600; color: var(--amber); background: var(--amber-bg, rgba(168, 100, 0, .08)); border: 1px solid var(--amber-bd);
}
.vem-root .fs-wcount.danger { color: var(--danger); background: var(--danger-bg, rgba(192, 57, 43, .07)); border-color: var(--danger-bd); }
.vem-root .fs-x {
  display: grid; place-items: center; width: 32px; height: 32px; padding: 0; border: 0; border-radius: 50%;
  background: none; color: var(--tx2); font-size: 14px; cursor: pointer;
}
.vem-root .fs-x:hover { background: var(--hover); color: var(--tx); }
.vem-root .fs-line { margin: 9px 0 0; padding: 1px 0 1px 10px; border-left: 2px solid var(--teal); font-size: 13px; color: var(--tx2); line-height: 1.45; }

/* cuerpo: dos columnas con su propio scroll */
.vem-root .fs-body { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr); min-height: 0; }
.vem-root .fs-body.one { grid-template-columns: minmax(0, 1fr); overflow-y: auto; }
.vem-root .fs-left { background: var(--panel2); overflow-y: auto; padding: 16px 16px 22px; border-right: 1px solid var(--line2); }
.vem-root .fs-right { overflow-y: auto; padding: 16px 20px 22px; min-width: 0; }
.vem-root .fs-body.one .fs-right { max-width: 860px; overflow: visible; }

.vem-root .fs-sec { margin: 0; padding: 0; border: 0; min-width: 0; }
.vem-root .fs-sec + .fs-sec { margin-top: 22px; }
.vem-root .fs-h {
  display: flex; align-items: center; gap: 8px; margin: 0 0 10px; padding: 0; font-family: var(--mono); font-size: 10.5px;
  font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--tx2); word-break: normal;
}
.vem-root .fs-h.run { color: var(--mint); }
.vem-root .fs-n {
  display: grid; place-items: center; flex: 0 0 19px; height: 19px; border-radius: 50%; letter-spacing: 0;
  background: var(--panel); border: 1px solid var(--mint-bd); color: var(--mint); font-size: 10px;
}
.vem-root .fs-glabel {
  margin: 0 0 8px; font-family: var(--mono); font-size: 9.5px; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--tx3);
}

/* cadena de preguntas */
.vem-root .fs-chain { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.vem-root .fs-q {
  display: flex; align-items: flex-start; gap: 10px; padding: 9px 8px 9px 10px; background: var(--panel); border: 1px solid var(--line2);
  border-radius: 11px; box-shadow: 0 1px 2px var(--line); outline: none; transition: border-color .15s ease, box-shadow .15s ease;
}
.vem-root .fs-q:hover { border-color: var(--port); }
.vem-root .fs-q:focus-visible { border-color: var(--mint); box-shadow: 0 0 0 3px var(--mint-bg); }
.vem-root .fs-q.sys { border-style: dashed; }
.vem-root .fs-q.dead { border-style: dashed; border-color: var(--amber-bd); background: var(--amber-bg, rgba(168, 100, 0, .08)); }
.vem-root .fs-qn {
  display: grid; place-items: center; flex: 0 0 22px; height: 22px; border-radius: 50%;
  background: var(--mint-bg); color: var(--mint); font-family: var(--mono); font-size: 11px; font-weight: 600;
}
.vem-root .fs-q.dead .fs-qn { background: var(--amber-bg, rgba(168, 100, 0, .08)); color: var(--amber); }
.vem-root .fs-qmain { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.vem-root .fs-ql { font-weight: 600; font-size: 13px; overflow-wrap: anywhere; }
.vem-root .fs-qa { font-family: var(--mono); font-size: 10.5px; color: var(--tx3); overflow-wrap: anywhere; }
.vem-root .fs-qcov { display: flex; align-items: center; gap: 7px; margin-top: 4px; font-size: 11.5px; color: var(--tx2); }
.vem-root .fs-q.dead .fs-qcov { color: var(--amber); }
.vem-root .fs-tag { margin-left: 6px; padding: 1px 5px; border-radius: 4px; background: var(--mute-bg); color: var(--tx2); font-family: var(--mono); font-size: 9px; font-weight: 500; vertical-align: 1px; }
.vem-root .fs-bar { width: 36px; height: 5px; border-radius: 3px; background: var(--mute-bg); overflow: hidden; }
.vem-root .fs-bar b { display: block; height: 100%; background: var(--cyan); }
.vem-root .fs-qbtns { display: flex; gap: 1px; opacity: .55; transition: opacity .15s ease; }
.vem-root .fs-q:hover .fs-qbtns, .vem-root .fs-q:focus-visible .fs-qbtns { opacity: 1; }
.vem-root .fs-ib {
  display: grid; place-items: center; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 7px;
  background: none; color: var(--tx2); font-size: 13px; cursor: pointer;
}
.vem-root .fs-ib:hover:not(:disabled) { background: var(--hover); color: var(--tx); }
.vem-root .fs-ib.del:hover { background: var(--danger-bg, rgba(192, 57, 43, .07)); color: var(--danger); }
.vem-root .fs-ib:disabled { opacity: .3; cursor: default; }
.vem-root .fs-pick {
  list-style: none; margin-top: 8px; padding: 8px; background: var(--panel); border: 1px solid var(--mint-bd);
  border-radius: 11px; box-shadow: var(--lift, 0 8px 24px -12px rgba(31, 35, 40, .25));
}
.vem-root .fs-chain .fs-pick { margin-top: 0; }
.vem-root .fs-add {
  display: flex; align-items: center; width: 100%; margin-top: 8px; padding: 9px 12px; text-align: left; cursor: pointer;
  background: none; border: 1px dashed var(--mint-bd); border-radius: 11px; color: var(--mint); font-weight: 600;
  transition: background .15s ease, border-color .15s ease;
}
.vem-root .fs-add:hover { background: var(--mint-bg); border-style: solid; }
.vem-root .fs-sugg { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 8px; font-size: 11.5px; color: var(--tx3); }
.vem-root .fs-sugg-l { color: var(--mint); font-weight: 600; }
.vem-root .fs-sugg-b {
  max-width: 100%; padding: 3px 10px; border: 1px solid var(--mint-bd); border-radius: 100px; background: var(--panel);
  color: var(--mint); font-size: 11.5px; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block;
}
.vem-root .fs-sugg-b:hover { background: var(--mint-bg); }
.vem-root .fs-hint { margin: 8px 2px 0; font-size: 11.5px; color: var(--tx3); }
.vem-root .fs-empty { margin: 0; padding: 10px 12px; border: 1px dashed var(--line2); border-radius: 11px; background: var(--panel); color: var(--tx2); font-size: 12.5px; }

/* tarjetas de regla y de «sin valor»: radios nativos ocultos a la vista */
.vem-root .fs-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.vem-root .fs-card {
  position: relative; display: flex; flex-direction: column; gap: 3px; padding: 10px 11px 9px; cursor: pointer;
  background: var(--panel); border: 1px solid var(--line2); border-radius: 11px;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}
.vem-root .fs-card:hover { border-color: var(--port); }
.vem-root .fs-card.on, .vem-root .fs-card:has(:checked) { border-color: var(--mint); background: var(--mint-bg); box-shadow: inset 0 0 0 1px var(--mint); }
.vem-root .fs-card:has(:focus-visible) { outline: 2px solid var(--mint); outline-offset: 2px; }
.vem-root .fs-radio { position: absolute; width: 1px; height: 1px; margin: 0; opacity: 0; pointer-events: none; clip-path: inset(50%); }
.vem-root .fs-ct { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; }
.vem-root .fs-dot { position: relative; flex: 0 0 14px; height: 14px; border-radius: 50%; border: 1.5px solid var(--port); background: var(--panel); transition: border-color .15s ease; }
.vem-root .fs-dot::after {
  content: ''; position: absolute; inset: 2.5px; border-radius: 50%; background: var(--mint); transform: scale(0); transition: transform .15s ease;
}
.vem-root .fs-card.on .fs-dot, .vem-root .fs-card:has(:checked) .fs-dot { border-color: var(--mint); }
.vem-root .fs-card.on .fs-dot::after, .vem-root .fs-card:has(:checked) .fs-dot::after { transform: scale(1); }
.vem-root .fs-cd { padding-left: 21px; font-size: 11.5px; color: var(--tx2); line-height: 1.35; }
.vem-root .fs-mini { padding-left: 21px; margin-top: 3px; font-family: var(--mono); font-size: 10.5px; color: var(--tx3); }
.vem-root .fs-mini b { color: var(--tx); }
.vem-root .fs-mini.warn, .vem-root .fs-mini.warn b { color: var(--amber); }

/* «Así se ejecutará» */
.vem-root .fs-groups { display: flex; flex-direction: column; }
.vem-root .fs-g { display: grid; grid-template-columns: 88px minmax(0, 1fr); gap: 12px; padding: 11px 0; border-top: 1px solid var(--line); }
.vem-root .fs-g:first-child { border-top: 0; padding-top: 2px; }
.vem-root .fs-gl { padding-top: 3px; font-family: var(--mono); font-size: 9.5px; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--tx3); }
.vem-root .fs-gb > * + * { margin-top: 7px; }
.vem-root .fs-s { margin: 0; font-size: 13px; line-height: 1.5; color: var(--tx); }
.vem-root .fs-s.lead { font-size: 13.5px; }
.vem-root .fs-s.info { display: flex; gap: 8px; font-size: 12px; color: var(--tx2); line-height: 1.45; }
.vem-root .fs-i {
  display: grid; place-items: center; flex: 0 0 15px; height: 15px; margin-top: 1px; border-radius: 50%; border: 1px solid var(--tx3);
  color: var(--tx3); font: 600 9px/1 var(--mono); font-style: normal;
}
.vem-root .fs-warn {
  display: flex; gap: 8px; margin: 0; padding: 7px 10px; border-left: 3px solid var(--amber); border-radius: 8px;
  background: var(--amber-bg, rgba(168, 100, 0, .08)); font-size: 12.5px; line-height: 1.45; color: var(--tx);
}
.vem-root .fs-warn b { flex: none; color: var(--amber); font-size: 11px; line-height: 18px; }
.vem-root .fs-warn.danger { border-color: var(--danger); background: var(--danger-bg, rgba(192, 57, 43, .07)); }
.vem-root .fs-warn.danger b { color: var(--danger); }
.vem-root .fs-samples { display: flex; flex-wrap: wrap; gap: 5px; margin: 0; padding: 0; list-style: none; }
.vem-root .fs-samples li {
  display: inline-flex; align-items: baseline; gap: 5px; padding: 2px 8px; border: 1px solid var(--line2); border-radius: 7px;
  background: var(--panel2); font-family: var(--mono); font-size: 11px; color: var(--tx2);
}
.vem-root .fs-samples li b { font-weight: 700; }
.vem-root .fs-samples li.ok b { color: var(--mint); }
.vem-root .fs-samples li.bad b { color: var(--danger); }
.vem-root .fs-samples li.warn { border-color: var(--amber-bd); }
.vem-root .fs-samples li.warn b { color: var(--amber); }
.vem-root .fs-samples .fs-sraw { color: var(--tx); }

/* por formulario */
.vem-root .fs-block { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--line2); }
.vem-root .fs-pft, .vem-root .fs-keyrows { list-style: none; margin: 0; padding: 0; border: 1px solid var(--line2); border-radius: 11px; overflow: hidden; background: var(--panel); }
.vem-root .fs-pfr {
  display: grid; grid-template-columns: 30px minmax(0, 1.15fr) minmax(0, 1.3fr) auto auto; gap: 10px; align-items: baseline;
  padding: 7px 10px; border-top: 1px solid var(--line); font-size: 12.5px;
}
.vem-root .fs-pfr:first-child, .vem-root .fs-keyrows li:first-child { border-top: 0; }
.vem-root .fs-p { justify-self: start; padding: 1px 5px; border-radius: 5px; background: var(--mint-bg); color: var(--mint); font-family: var(--mono); font-size: 10px; font-weight: 600; }
.vem-root .fs-pfn { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vem-root .fs-pfu { color: var(--tx2); overflow-wrap: anywhere; }
.vem-root .fs-pfm { color: var(--tx3); font-size: 11.5px; white-space: nowrap; }
.vem-root .fs-pfm.danger { color: var(--danger); }
.vem-root .fs-pfe { display: inline-flex; gap: 6px; align-items: center; justify-self: end; }
.vem-root .fs-pfr.silent .fs-pfu { color: var(--tx3); }
.vem-root .fs-pfr.off, .vem-root .fs-pfr.missing { background: var(--panel2); }
.vem-root .fs-pfr.off .fs-pfn, .vem-root .fs-pfr.missing .fs-pfn { color: var(--tx2); font-weight: 500; }
.vem-root .fs-pfr.off .fs-p, .vem-root .fs-pfr.missing .fs-p { background: var(--mute-bg); color: var(--tx3); }
.vem-root .fs-pfr.missing .fs-pfu { color: var(--amber); }
.vem-root .fs-lead {
  padding: 2px 8px; border-radius: 100px; background: var(--teal); color: #fff; font-family: var(--mono); font-size: 9.5px;
  font-weight: 600; letter-spacing: .1em; text-transform: uppercase;
}
.vem-root .fs-show { padding: 0 2px; border: 0; background: none; color: var(--mint); font-size: 12px; font-weight: 600; cursor: pointer; }
.vem-root .fs-pfsub { grid-column: 2 / -1; margin: 2px 0 0; padding: 0 0 0 14px; font-size: 12px; color: var(--tx2); }
.vem-root .fs-noex { margin: 18px 0 0; padding: 10px 12px; border: 1px dashed var(--amber-bd); border-radius: 10px; color: var(--amber); font-size: 12.5px; }

/* hoja de la clave */
.vem-root .fs-body.one .fs-s.lead { margin-bottom: 16px; }
.vem-root .fs-keyrows { margin-bottom: 16px; }
.vem-root .fs-keyrows li {
  display: grid; grid-template-columns: 30px minmax(0, 1fr) minmax(0, 1.4fr) auto; gap: 10px; align-items: center;
  padding: 8px 10px; border-top: 1px solid var(--line); font-size: 12.5px;
}
.vem-root .fs-keyrows li.danger { background: var(--danger-bg, rgba(192, 57, 43, .07)); }
.vem-root .fs-keyrows li.danger .fs-kt { color: var(--danger); font-weight: 600; }
.vem-root .fs-kt { color: var(--tx2); overflow-wrap: anywhere; }
.vem-root .btn.fs-sm { min-height: 30px; padding: 4px 10px; font-size: 12px; }

/* notas plegables */
.vem-root .fs-notes { margin-top: 18px; border: 1px solid var(--line2); border-radius: 11px; background: var(--panel2); }
.vem-root .fs-notes summary {
  display: flex; align-items: center; gap: 8px; padding: 9px 12px; list-style: none; cursor: pointer;
  font-size: 12.5px; font-weight: 600; color: var(--tx2);
}
.vem-root .fs-notes summary::-webkit-details-marker { display: none; }
.vem-root .fs-notes summary::before { content: '▸'; color: var(--tx3); transition: transform .15s ease; }
.vem-root .fs-notes[open] summary::before { transform: rotate(90deg); }
.vem-root .fs-notes ul { margin: 0; padding: 0 14px 12px 32px; font-size: 12px; color: var(--tx2); line-height: 1.45; }
.vem-root .fs-notes li + li { margin-top: 5px; }

/* pie */
.vem-root .fs-foot { display: flex; flex-direction: column; gap: 8px; padding: 10px 16px 12px 18px; border-top: 1px solid var(--line2); background: var(--panel); }
.vem-root .fs-past { display: flex; gap: 8px; margin: 0; font-size: 12px; color: var(--tx2); line-height: 1.4; }
.vem-root .fs-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vem-root .fs-tools { display: flex; gap: 8px; }
.vem-root .fs-tools:empty { display: none; }
.vem-root .fs-done { min-width: 112px; }

/* anchos del editor (no de la ventana: embebido, el editor es más estrecho) */
@container fs (max-width: 900px) {
  .vem-root .fs-body { grid-template-columns: minmax(0, 1fr); overflow-y: auto; }
  .vem-root .fs-left, .vem-root .fs-right { overflow: visible; }
  .vem-root .fs-left { border-right: 0; border-bottom: 1px solid var(--line2); }
}
@container fs (max-width: 760px) {
  .vem-root .fs { position: absolute; inset: 0; width: auto; height: auto; border-radius: 0; border-width: 3px 0 0; }
  .vem-root .fs-cards { grid-template-columns: minmax(0, 1fr); }
  .vem-root .fs-g { grid-template-columns: minmax(0, 1fr); gap: 4px; }
  .vem-root .fs-pfr, .vem-root .fs-keyrows li { grid-template-columns: 30px minmax(0, 1fr); }
  .vem-root .fs-pfr > :nth-child(n + 3), .vem-root .fs-keyrows li > :nth-child(n + 3) { grid-column: 2; justify-self: start; }
}
</style>
